import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { CSESlideIn } from '@cloudsuite/mosaic-elements'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { CSCCreateOrderTemplate } from 'components/discovery/pdp/create-order-template/create-order-template.js'
import { type CreateOrderTemplateResponse } from 'components/discovery/pdp/create-order-template/api/create-order-template-api.js'
import 'components/discovery/product/add-to-order-templates/add-to-order-templates.js'
import 'components/discovery/pdp/create-order-template/create-order-template.js'
import { ORDER_TEMPLATES_STORE, type OrderTemplatesStore } from 'components/discovery/shared/order-templates/stores/order-templates-store.js'
import { InternalProduct } from 'components/discovery/product/tile-parts/tile-parts-typing.js'
import { track } from 'components/shared/analytics/analytics.js'
import type { AnalyticsContext } from 'components/shared/analytics/analytics-types.js'
import { DEFAULT_ROLE_PERMISSIONS, RolePermissionsContext, type RolePermissions } from 'components/shared/role/role-provider.js'
import { CSCLoginSlideIn, PageLoginSlideInContext } from 'components/shared/login/login-slide-in.js'
import 'components/shared/login/login-slide-in.js'
import { style } from './order-template-slide-in.style.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { orderTemplateQuantity } from 'components/discovery/product/add-to-order-templates/order-template-quantity.js'

initializeIconsByRole({
  'csc-order-template-slide-in.add': { name: 'plus', style: 'regular' },
})

export class OrderTemplateLoginRequiredEvent extends Event {
  static type = 'csc-order-template-login-required' as const

  constructor() {
    super(OrderTemplateLoginRequiredEvent.type, { bubbles: true, composed: true, cancelable: true })
  }
}

declare global {
  interface GlobalEventHandlersEventMap {
    [OrderTemplateLoginRequiredEvent.type]: OrderTemplateLoginRequiredEvent,
  }
}

export const triggerVariant = ['primary', 'secondary', 'white'] as const
export type triggerVariantTypes = typeof triggerVariant

@customElement('csc-order-template-slide-in')
export class CSCOrderTemplateSlideIn extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Number, attribute: 'product-id' })
  productId = 0

  @property({ type: Number })
  quantity = 0

  @property({ type: String, attribute: 'trigger-type' })
  triggerType: 'icon-button' | 'link' = 'link'

  @property({ type: String, attribute: 'trigger-variant' })
  triggerVariant: triggerVariantTypes[number] = 'primary'

  @property({ type: String, attribute: 'trigger-size' })
  triggerSize: 'default' | 'small' = 'default'

  @property({ type: String, attribute: 'trigger-icon-name' })
  triggerIconName = 'star'

  @property({ attribute: false })
  trackingProduct: InternalProduct | null = null

  /** @deprecated Use `trackingProduct` instead: the product is identified by `product-id`, this object is only sent to analytics. */
  @property({ type: Object })
  set product(value: InternalProduct | null) {
    console.warn('<csc-order-template-slide-in> "product" is deprecated, use the "trackingProduct" property instead: the product is identified by "product-id".')
    this.trackingProduct = value
  }

  get product() {
    return this.trackingProduct
  }

  @property({ attribute: false })
  trackingContext: AnalyticsContext = {}
  @inject(ORDER_TEMPLATES_STORE)
  private store!: OrderTemplatesStore

  @inject(PDP_PRODUCT_STORE)
  private pdpProductStore!: PdpProductStore

  @consume({ context: RolePermissionsContext, subscribe: true })
  @state()
  private rolePermissions: RolePermissions = DEFAULT_ROLE_PERMISSIONS

  @consume({ context: PageLoginSlideInContext, subscribe: true })
  @state()
  private pageLoginSlideIn: CSCLoginSlideIn | null = null

  @query('#templates-slide-in')
  private slideIn!: CSESlideIn

  @query('#create-slide-in')
  private createSlideIn!: CSESlideIn

  @query('csc-create-order-template')
  private createForm!: CSCCreateOrderTemplate

  @query('csc-login-slide-in')
  private loginSlideIn!: CSCLoginSlideIn

  @state()
  private templatesRendered = false

  @state()
  private loginRendered = false

  @state()
  private canCreateTemplate = false

  @state()
  private creatingTemplate = false

  @state()
  private addAfterCreateFailed = false

  private handleCreateStateChanged(e: CustomEvent<{ canSubmit: boolean, loading: boolean }>) {
    this.canCreateTemplate = e.detail.canSubmit
    this.creatingTemplate = e.detail.loading
  }

  private handleTriggerClick(e: Event) {
    e.stopPropagation()
    void this.open()
  }

  async open() {
    if (!this.rolePermissions.loggedIn && !this.dispatchEvent(new OrderTemplateLoginRequiredEvent())) return
    if (!this.rolePermissions.loggedIn && this.pageLoginSlideIn) {
      await this.pageLoginSlideIn.open()
      return
    }
    if (!this.rolePermissions.loggedIn) {
      this.loginRendered = true
      await this.updateComplete
      await this.loginSlideIn.open()
      return
    }
    this.templatesRendered = true
    await this.updateComplete
    await this.slideIn.open()
  }

  private handleCreateClick() {
    this.slideIn.close()
    this.createSlideIn.open()
    requestAnimationFrame(() => this.createForm.focusNameInput())
  }

  private handleCancelCreate() {
    this.createSlideIn.close()
    this.slideIn.open()
  }

  private handleAddClick() {
    void this.createForm.submit()
  }

  private async handleTemplateCreated(e: CustomEvent<CreateOrderTemplateResponse>) {
    this.createSlideIn.close()
    this.slideIn.open()
    this.addAfterCreateFailed = false
    const templateId = e.detail.id
    try {
      await this.store.refetchAfter(async () => {
        if (!templateId) return
        await this.store.addProductToTemplate(templateId, this.productId, orderTemplateQuantity(this.quantity, this.productId, this.pdpProductStore.product()))
        if (this.isConnected) this.trackAddedToWishlist()
      })
    } catch (error) {
      console.error('Failed to add the product to the new order template', error)
      if (!this.isConnected) return
      this.addAfterCreateFailed = true
    }
  }

  private trackAddedToWishlist() {
    if (!this.trackingProduct) return
    track({ type: 'product-added-to-wishlist', product: this.trackingProduct, context: this.trackingContext })
  }

  private renderTrigger() {
    if (this.triggerType === 'icon-button') {
      return html`
        <cse-icon-button
          id="trigger"
          variant="${this.triggerVariant}"
          size="${this.triggerSize}"
        >
          <cse-icon icon-name="${this.triggerIconName}" icon-style="light"></cse-icon>
        </cse-icon-button>
      `
    }

    return html`
      <cse-link
        id="trigger"
        variant="${this.triggerVariant}"
        size="${this.triggerSize}"
        font-variant="${this.triggerSize === 'small' ? 'body-s' : 'body'}"
      >
        <cse-icon slot="prefix" icon-name="${this.triggerIconName}" icon-style="light"></cse-icon>${t('open_button', { ns: 'csc-order-template-slide-in', default: 'Add to order templates' })}
      </cse-link>
    `
  }

  private renderLogin() {
    if (!this.loginRendered) return nothing
    return html`<csc-login-slide-in></csc-login-slide-in>`
  }

  private renderTemplates() {
    if (!this.templatesRendered) return nothing
    return html`
      <cse-slide-in id="templates-slide-in">
        <div slot="header">
          ${t('header', { ns: 'csc-order-template-slide-in', default: 'Add to order template' })}
        </div>

        ${this.addAfterCreateFailed ? html`
          <cse-alert variant="error">
            ${t('error_add_to_new_template', { ns: 'csc-order-template-slide-in', default: 'The product could not be added to your new order template. Please try again.' })}
          </cse-alert>
        ` : nothing}
        <csc-add-to-order-templates
          product-id="${this.productId}"
          quantity="${this.quantity}"
          .displayLimit="${Number.POSITIVE_INFINITY}"
          .trackingProduct="${this.trackingProduct}"
          .trackingContext="${this.trackingContext}"
        ></csc-add-to-order-templates>

        <cse-button slot="footer" fit="fill" variant="primary" @click="${this.handleCreateClick}">
          <cse-icon slot="prefix" icon-role="csc-order-template-slide-in.add"></cse-icon>
          ${t('create_template', { ns: 'csc-order-template-slide-in', default: 'Create new template' })}
        </cse-button>
      </cse-slide-in>

      <cse-slide-in id="create-slide-in">
        <div slot="header">
          ${t('create_header', { ns: 'csc-order-template-slide-in', default: 'Create new template' })}
        </div>

        <csc-create-order-template
          hidden-fields="submit-button"
          @create-state-changed="${this.handleCreateStateChanged}"
          @order-template-created="${this.handleTemplateCreated}"
        ></csc-create-order-template>

        <cse-button slot="footer" fit="fill" variant="default" @click="${this.handleCancelCreate}">
          ${t('cancel_button', { ns: 'csc-order-template-slide-in', default: 'Cancel' })}
        </cse-button>
        <cse-button
          slot="footer"
          fit="fill"
          variant="primary"
          ?disabled="${!this.canCreateTemplate}"
          ?loading="${this.creatingTemplate}"
          @click="${this.handleAddClick}"
        >
          ${t('add_button', { ns: 'csc-order-template-slide-in', default: 'Add' })}
        </cse-button>
      </cse-slide-in>
    `
  }

  override render() {
    return html`
      <slot name="trigger" @click="${this.handleTriggerClick}">${this.renderTrigger()}</slot>
      ${this.renderTemplates()}
      ${this.renderLogin()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-order-template-slide-in': CSCOrderTemplateSlideIn
  }
}
